import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Typography, TextField } from '@mui/material';
import { useIntl } from 'react-intl';
import { RowSsrObjectType } from '../../../types/ancillaryCostTableRowTypes';
import AdjustedSurchargeCell from './AdjustedSurchargeCell';
import { useBookingContext } from '../../../hooks/use-booking-context';

interface props {
  row: RowSsrObjectType;
  index: string;
  commonStyles: any;
  rebooking: boolean | undefined;
  surchargeValues: { [key: string]: string };
  handleInputChange: (id: any, value: any) => void;
  showAdjustedSurcharge: boolean | undefined;
}

const SsrsComparativeRow: React.FC<props> = ({
  row,
  index,
  commonStyles,
  rebooking,
  surchargeValues,
  handleInputChange,
  showAdjustedSurcharge,
}) => {
  const intl = useIntl();
  const { costFormatter, numberFormatter, decimalSeparator, allowedCharsRegex } = useBookingContext();
  const zeroValue = numberFormatter.format(0);

  const getCellStyles = (row: string, key: string) => ({
    ...commonStyles.cellHeader,
    backgroundColor: row !== '' ? (key === 'original' ? '#F3F0EC' : '#E2F5FD') : 'white',
    borderTop: '1px solid #737373',
  });

  return (
    <TableRow key={index}>
      <TableCell align="left" sx={{ ...commonStyles.cellBody, whiteSpace: 'nowrap', border: 'none' }}>
        <Typography>{row.label}</Typography>
      </TableCell>
      <TableCell align="left" sx={getCellStyles(row.original, 'original')}>
        <Typography sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {row.rebooking ? (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontWeight: 'bold' }}>{row.original.split('/')[0]}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{row.original.split('/')[1]}</span>
                <span>{costFormatter.format(row.originalPrice)}</span>
              </div>
            </div>
          ) : (
            <>
              <span>{row.original}</span>
              <span>{row.label && costFormatter.format(row.originalPrice)}</span>
            </>
          )}
        </Typography>
      </TableCell>
      <TableCell align="left" sx={getCellStyles(row.original, 'new')}>
        <Typography sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {row.rebooking ? (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ fontWeight: 'bold' }}>{row.new.split('/')[0]}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{row.new.split('/')[1]}</span>
                <span>{costFormatter.format(row.newPrice)}</span>
              </div>
            </div>
          ) : (
            <>
              <span>{row.new}</span>
              {row.new === `${intl.formatMessage({ id: 'ndc.rebookServiceFee' })}` ? (
                <TextField
                  inputProps={{
                    style: {
                      width: '100px',
                      height: '31px',
                      padding: '4px',
                      lineHeight: '23.4px',
                      fontSize: '18px',
                      textAlign: 'right',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                  value={surchargeValues['Service Fee'] ?? zeroValue}
                  onChange={e => {
                    const numericValue =
                      e.target.value !== '' && e.target.value !== zeroValue
                        ? e.target.value.replace(allowedCharsRegex, '')
                        : zeroValue;
                    handleInputChange('Service Fee', numericValue);
                  }}
                  onBlur={e => {
                    const numericValue = e.target.value.replace(allowedCharsRegex, '');
                    const parsedValue = parseFloat(numericValue.replace(decimalSeparator, '.'));
                    const formattedValue = costFormatter.format(parsedValue);
                    handleInputChange('Service Fee', formattedValue);
                  }}
                />
              ) : (
                <span>{row.label && costFormatter.format(row.newPrice)}</span>
              )}
            </>
          )}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ borderTop: '1px solid #737373' }}>
        <Typography sx={{ textAlign: rebooking ? 'center' : 'right' }}>
          {row.label && costFormatter.format(row.surcharge)}
        </Typography>
      </TableCell>
      {showAdjustedSurcharge && (
        <AdjustedSurchargeCell row={row} surchargeValues={surchargeValues} handleInputChange={handleInputChange} />
      )}
    </TableRow>
  );
};
export default SsrsComparativeRow;
